import React, { FC, useMemo } from "react";
import "react-data-grid/lib/styles.css";
import DataGrid from "react-data-grid";
import { Button, Grid, Paper, Typography, CircularProgress } from "@mui/material";
import classes from "./combinations.module.css";
import FileButton from "../../components/file-button";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useCombinationsLayout } from "../../hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import useCombinationsLogic from "./useCombinationsLogic";

const ResponsiveGridLayout = WidthProvider(Responsive);

const Combinations: FC = () => {
    const {
        //vars
        columns,
        //states
        combinations,

        output1,
        output2,
        output3,
        result,
        isFormulationsHidden,

        //loading
        importLoading,
        exportLoading,

        //functions
        generateOutputs,
        onRowsChange,
        handleFileChange,
        setIsFormulationsHidden,
    } = useCombinationsLogic();

    const { gridLayoutProps } = useCombinationsLayout();
    const combRows = useMemo(() => Object.values(combinations), [combinations]);

    return (
        <main>
            <Grid container spacing={2} alignItems={"flex-end"} style={{ width: "100%" }}>
                <Grid item xs={12}>
                    <FileButton
                        disabled={importLoading}
                        onChange={handleFileChange}
                        // onClear={handleClearFile}
                        // clearable={field.value !== null}
                        accept="excel"
                    >
                        {importLoading ? (
                            <CircularProgress style={{ color: "white", width: 25, height: 25 }} />
                        ) : (
                            "Import Excel"
                        )}
                    </FileButton>
                </Grid>
                {/* Display the Combinations Data Grid */}
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Typography variant="h4" align="center">
                            Combinations Data Grid
                        </Typography>
                        <DataGrid
                            columns={columns}
                            className={"rdg-light"} // "rdg-dark"
                            rows={combRows}
                            rowKeyGetter={(row) => `${row.variable}-comb-row`}
                            onRowsChange={onRowsChange}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} style={{ marginTop: 15 }}>
                    <Button
                        disabled={exportLoading}
                        variant="contained"
                        color="secondary"
                        onClick={() => generateOutputs()}
                    >
                        {exportLoading ? (
                            <CircularProgress style={{ color: "white", width: 25, height: 25 }} />
                        ) : (
                            "Generate & Export Outputs"
                        )}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        style={{ marginLeft: 5 }}
                        onClick={() => setIsFormulationsHidden((prev) => !prev)}
                    >
                        {isFormulationsHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </Button>
                </Grid>
            </Grid>

            <ResponsiveGridLayout {...gridLayoutProps}>
                <div key="maximization" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                    <Paper elevation={3} className={classes.card}>
                        <Typography variant="h4" align="center" className={classes.cardTitle}>
                            1st Output (Objective Function)
                        </Typography>
                        <Typography variant="body1" align="center">
                            {output1}
                        </Typography>
                    </Paper>
                </div>

                <div key="route-constraints" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                    <Paper elevation={3} className={classes.card}>
                        <Typography variant="h4" align="center" className={classes.cardTitle}>
                            2nd Output (Route Constraints)
                        </Typography>
                        <Typography variant="body1" align="center">
                            {output2}
                        </Typography>
                    </Paper>
                </div>
                <div key="wh-capacity-constraints" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                    <Paper elevation={3} className={classes.card}>
                        <Typography variant="h4" align="center" className={classes.cardTitle}>
                            3rd Output (Warehouse Capacity Constraints)
                        </Typography>
                        <Typography variant="body1" align="center">
                            {output3.split("\n").map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </Typography>
                    </Paper>
                </div>
                <div key="solution" style={{ display: isFormulationsHidden ? "none" : "block" }}>
                    <Paper elevation={3} className={classes.card}>
                        <Typography variant="h4" align="center" className={classes.cardTitle}>
                            Optimal Solution
                        </Typography>
                        <Typography variant="body1" align="center">
                            {result.optimal_values ?? ""}
                            <br />
                            {result.optimal_value ?? ""}
                        </Typography>
                    </Paper>
                </div>
            </ResponsiveGridLayout>
        </main>
    );
};

export default Combinations;
